import { Text, type TextTags, type TextOwnProps } from '../Text';

export interface HeadingsProps extends Omit<TextOwnProps, 'variant'> {
  /**
   * The content of the component
   */
  children: React.ReactNode;
  /**
   * It defines the HTML tag that will be rendered
   */
  as?: TextTags;
}

type H1Props = Omit<HeadingsProps, 'textOverflowEllipsis'>;

/**
 * The Heading components render text with big and bold styles and should be used carefully.
 * For SEO reasons, each page should only have one `h1` tag per page, so use the `as` prop to make sure you're using the right tag.
 */
export const H1PlusLarge: React.FC<H1Props> = ({
  children,
  as = 'h1',
  ...rest
}) => {
  return (
    <Text as={as} variant="h1plus-large" {...rest}>
      {children}
    </Text>
  );
};

export const H1PlusSmall: React.FC<H1Props> = ({
  children,
  as = 'h1',
  ...rest
}) => {
  return (
    <Text as={as} variant="h1plus-small" {...rest}>
      {children}
    </Text>
  );
};

export const H1: React.FC<H1Props> = ({ children, as = 'h1', ...rest }) => {
  return (
    <Text as={as} variant="h1" {...rest}>
      {children}
    </Text>
  );
};

export const H2: React.FC<HeadingsProps> = ({
  children,
  as = 'h2',
  textOverflowEllipsis = false,
  ...rest
}) => {
  return (
    <Text
      as={as}
      textOverflowEllipsis={textOverflowEllipsis}
      variant="h2"
      {...rest}
    >
      {children}
    </Text>
  );
};

export const H3: React.FC<HeadingsProps> = ({
  children,
  as = 'h3',
  textOverflowEllipsis = false,
  ...rest
}) => {
  return (
    <Text
      as={as}
      textOverflowEllipsis={textOverflowEllipsis}
      variant="h3"
      {...rest}
    >
      {children}
    </Text>
  );
};

export const H4: React.FC<HeadingsProps> = ({
  children,
  as = 'h4',
  textOverflowEllipsis = false,
  ...rest
}) => {
  return (
    <Text
      as={as}
      textOverflowEllipsis={textOverflowEllipsis}
      variant="h4"
      {...rest}
    >
      {children}
    </Text>
  );
};

export const H5: React.FC<HeadingsProps> = ({
  children,
  as = 'h5',
  textOverflowEllipsis = false,
  ...rest
}) => {
  return (
    <Text
      as={as}
      textOverflowEllipsis={textOverflowEllipsis}
      variant="h5"
      {...rest}
    >
      {children}
    </Text>
  );
};

export const H6: React.FC<HeadingsProps> = ({
  children,
  as = 'h6',
  textOverflowEllipsis = false,
  ...rest
}) => {
  return (
    <Text
      as={as}
      textOverflowEllipsis={textOverflowEllipsis}
      variant="h6"
      {...rest}
    >
      {children}
    </Text>
  );
};

export const H6Plus: React.FC<HeadingsProps> = ({
  children,
  as = 'h6',
  textOverflowEllipsis = false,
  ...rest
}) => {
  return (
    <Text
      as={as}
      textOverflowEllipsis={textOverflowEllipsis}
      variant="h6plus"
      {...rest}
    >
      {children}
    </Text>
  );
};

export const H7: React.FC<HeadingsProps> = ({
  children,
  as = 'h6',
  textOverflowEllipsis = false,
  ...rest
}) => {
  return (
    <Text
      as={as}
      textOverflowEllipsis={textOverflowEllipsis}
      variant="h7"
      {...rest}
    >
      {children}
    </Text>
  );
};
